import type {PageObject} from '@PosterWhiteboard/page/page.types';
import {AnimationType, ITEM_TYPE} from '@PosterWhiteboard/items/item/item.types';
import type {SlideshowItemObject} from '@PosterWhiteboard/items/slideshow-item/slideshow-item.class';
import type {AudioItemAudioPlayerObject} from '@PosterWhiteboard/classes/audio-clips/audio-item.class';
import type {AudioClipsObject} from '@PosterWhiteboard/classes/audio-clips/audio-clips.class';

export const getPagesArray = (pagesHashMap: Record<string, PageObject> | undefined, pageOrder: string[] | undefined): PageObject[] => {
  if (!pagesHashMap || !pageOrder) {
    return [];
  }
  return pageOrder.map((id) => pagesHashMap[id]).filter((page): page is PageObject => page !== undefined);
};

export const getPageStartAndEndTime = (pages: PageObject[], pageId: string): [number, number] | undefined => {
  let startTime = 0;

  for (const page of pages) {
    if (page.hashedID === pageId) {
      const endTime = startTime + (page.duration ?? 0);
      return [startTime, endTime];
    }
    startTime += page.duration ?? 0;
  }

  return undefined;
};

export const hasPageAnimation = (page: PageObject): boolean => {
  return page.introAnimation.type !== AnimationType.NONE || page.outroAnimation.type !== AnimationType.NONE;
};

export const hasItems = (page: PageObject): boolean => {
  return Object.keys(page.items.itemsHashMap).length > 0;
};

export const pageItemHasAnimation = (page: PageObject): boolean => {
  return Object.values(page.items.itemsHashMap).some((item) => item.introAnimation.type !== AnimationType.NONE || item.outroAnimation.type !== AnimationType.NONE);
};

export const getAudioPlaybackDuration = (audioPlayer: AudioItemAudioPlayerObject): number => {
  const trimmedDuration = audioPlayer.trim.isTrimmed ? audioPlayer.trim.endTime - audioPlayer.trim.startTime : audioPlayer.originalDuration;
  const trimmedPlaybackDuration = trimmedDuration / audioPlayer.speed;
  return audioPlayer.playCycles * trimmedPlaybackDuration;
};

export const hasAudioClipsOnPage = (audioClips: AudioClipsObject | undefined, pageStartTime: number, pageEndTime: number): boolean => {
  if (!audioClips) {
    return false;
  }

  for (const audioItem of Object.values(audioClips.audioItemsHashMap)) {
    const audioStartTime = audioItem.onPosterStartTime;
    const playbackDuration = getAudioPlaybackDuration(audioItem.audioPlayer);
    const audioEndTime = audioItem.onPosterStartTime + playbackDuration;

    if (audioStartTime < pageEndTime && audioEndTime > pageStartTime) {
      return true;
    }
  }

  return false;
};

export const isSlideshowStreaming = (slideshowItem: SlideshowItemObject): boolean => {
  const slides = slideshowItem.slides;

  if (!slides) {
    return false;
  }

  const hasVideoSlide = Object.values(slides.slidesHashMap).some((slide) => slide.gitype === ITEM_TYPE.VIDEOSLIDE);
  const isMultiSlide = Object.keys(slides.slidesHashMap).length > 1;
  const hasIntroDelay = slideshowItem.onPageStartTime > 0;
  const hasSingleSlide = Object.keys(slides.slidesHashMap).length === 1;
  const hasAnimation = slideshowItem.introAnimation?.type !== AnimationType.NONE || slideshowItem.outroAnimation?.type !== AnimationType.NONE;
  const hasTransition = slideshowItem.transition?.type !== AnimationType.NONE;

  return hasVideoSlide || isMultiSlide || hasIntroDelay || (hasSingleSlide && hasAnimation && hasTransition);
};

export const pageHasStreamingItem = (page: PageObject): boolean => {
  for (const item of Object.values(page.items.itemsHashMap)) {
    if (
      item.gitype === ITEM_TYPE.VIDEO ||
      item.gitype === ITEM_TYPE.STICKER ||
      item.gitype === ITEM_TYPE.TRANSCRIPT ||
      (item.gitype === ITEM_TYPE.SLIDESHOW && isSlideshowStreaming(item as SlideshowItemObject))
    ) {
      return true;
    }
  }

  return false;
};

export const isPageVideo = (page: PageObject, audioClips: AudioClipsObject | undefined, pageStartTime: number, pageEndTime: number): boolean => {
  if ((hasPageAnimation(page) && hasItems(page)) || pageItemHasAnimation(page)) {
    return true;
  }

  if (hasAudioClipsOnPage(audioClips, pageStartTime, pageEndTime)) {
    return true;
  }

  return pageHasStreamingItem(page);
};
